import { enqueueSnackbar } from 'notistack';
import { create } from 'zustand';

import { authRepository } from 'src/auth/repositories/auth.repository';
import { setSession, clearSession, getStoredTokens } from 'src/auth/utils/utils';
import { useMeStore } from 'src/store/me/me.store';
import { getSiteName } from 'src/utils/host-name';
import { setSentryUser, clearSentryUser } from 'src/init-sentry';

type States = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
};

type Actions = {
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  refreshAccessToken: () => Promise<string | null>;
  login: (username: string, password: string) => Promise<any>;
  register: (name: string, email: string, password: string) => Promise<any>;
  logout: () => void;
};

type AuthStore = States & Actions;

const initialState: States = {
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

  setLoading: (isLoading) => set({ isLoading }),

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const authData = await authRepository.login({ email, password, host: getSiteName() });

      // Definir sessão e estado de autenticação
      setSession(authData.accessToken, authData.refreshToken);

      // Definir dados do usuário diretamente do adminUser
      const meStore = useMeStore.getState();
      meStore.setMeFromLogin(authData.adminUser);

      // Setar dados do usuário no Sentry
      if (authData.adminUser) {
        setSentryUser({
          id: authData.adminUser.id,
          email: authData.adminUser.email,
          name: authData.adminUser.displayName,
        });
      }

      set({
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return authData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro no login';
      set({
        isLoading: false,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  register: async (name: string, email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const authData = await authRepository.register({ name, email, password });

      // Definir sessão e estado de autenticação
      setSession(authData.accessToken, authData.refreshToken);
      set({
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      enqueueSnackbar('Conta criada com sucesso!', {
        variant: 'success',
      });

      return authData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro no registro';
      set({
        isLoading: false,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  refreshAccessToken: async () => {
    const { refreshToken } = getStoredTokens();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authRepository.refreshAccessToken(refreshToken);

      // Atualiza a sessão com ambos os tokens (novo accessToken e refreshToken)
      setSession(response.accessToken, response.refreshToken);

      return response.accessToken;
    } catch (error) {
      // Se falhar, limpa a sessão
      setSession(null, null);
      throw error;
    }
  },

  logout: () => {
    clearSession();
    set({
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });

    // Limpar dados do usuário quando fizer logout
    const meStore = useMeStore.getState();
    meStore.clearMe();

    // Limpar dados do usuário no Sentry
    clearSentryUser();
  },
}));
